import { Link, useLocation } from 'react-router';
import MaybeLink from '@/components/MaybeLink';
import { useLocale } from '@/hooks/useLocale';
import { appLoginUrl } from '@/config/appLinks';
import { NAV_EN } from '@/copy/nav-en';
import { NAV_AR } from '@/copy/nav-ar';
import IconGlobe from './icons/IconGlobe';
import './Drawer.css';

/* React 18.3 ships the `inert` attribute at runtime (per Decisions §D3) but
 * @types/react 18.3.31 doesn't yet expose it on HTMLAttributes. Augment here
 * so the empty-string form locked at §D3 typechecks. */
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    inert?: '' | undefined;
  }
}

/* Drawer is a pure presentation component. Close affordances (Escape, close-X
 * in the topbar hamburger) live in the parent's useDrawer hook — the drawer
 * has no internal close UI per canvas user-lock 2026-05-24 ("drawer IS the
 * backdrop, no separate scrim"). */
interface DrawerProps {
  isOpen: boolean;
  drawerId: string;
}

export default function Drawer({ isOpen, drawerId }: DrawerProps) {
  const { locale, strings } = useLocale();
  const { pathname } = useLocation();
  const copy = locale === 'ar' ? NAV_AR : NAV_EN;
  const target = locale === 'ar' ? 'en' : 'ar';
  const swapPath = pathname.replace(/^\/(ar|en)/, `/${target}`);
  const otherLabel = target === 'ar' ? 'العربية' : 'English';

  return (
    <div
      id={drawerId}
      className={`mkt-drawer${isOpen ? ' is-open' : ''}`}
      role="dialog"
      aria-modal={isOpen ? 'true' : undefined}
      aria-labelledby={`${drawerId}-title`}
      aria-hidden={!isOpen}
      inert={!isOpen ? '' : undefined}
    >
      <div className="mkt-drawer__sheet">
        <h2 id={`${drawerId}-title`} className="mkt-drawer__sr-title">
          {strings.nav.menu}
        </h2>
        <nav className="mkt-drawer__nav" aria-label={strings.nav.menu}>
          {copy.links.map((link) => (
            <MaybeLink key={link.href} to={link.href} live={link.live} external={link.external}>
              {link.label}
            </MaybeLink>
          ))}
        </nav>
        <div className="mkt-drawer__footer">
          {/* Locale SWAP — raw <Link>, NOT <LocaleLink>. LocaleLink injects the CURRENT
              locale prefix (see src/components/LocaleLink.tsx:12-13), which defeats a
              swap. The desktop locale switcher in TopNav.tsx uses raw <Link
              to={swapPath}> for the same reason. swapPath already carries the target
              locale prefix from the regex replace above. */}
          <Link
            to={swapPath}
            className="mkt-drawer__btn mkt-drawer__btn--locale"
            hrefLang={target}
            aria-label={otherLabel}
            /* Globe sits per the target language's reading direction — see
               TopNav locale switch for rationale. */
            dir={target === 'ar' ? 'rtl' : 'ltr'}
          >
            <span className="mkt-drawer__btn-icon">
              <IconGlobe size={17} />
            </span>
            <span>{otherLabel}</span>
          </Link>
          <a href={appLoginUrl(locale)} className="mkt-drawer__btn mkt-drawer__btn--login">
            {strings.nav.signin}
          </a>
        </div>
      </div>
    </div>
  );
}
